<template>
  <SectionContainer :colors="colors" :background-variant="backgroundVariant" class="w-full">
    <div class="flex flex-col items-center justify-center w-full" :style="cssVars">
      <div class="max-w-7xl mx-auto text-center">
        <component :is="headingTag" class="text-3xl md:text-4xl font-bold text-[var(--text-primary)] capitalize">
          {{ title }}
        </component>
        <p class="text-lg text-[var(--text-secondary)] mt-2 whitespace-pre-line" v-if="description">
          {{ description }}
        </p>
      </div>

      <!-- Grid Layout -->
      <div class="mt-10 grid gap-6" :class="gridColumns">
        <component
            :is="cardComponent"
            v-for="(item, index) in items"
            :key="index"
            v-motion
            :initial="{ opacity: 0, y: 30 }"
            :visible="{ opacity: 1, y: 0 }"
            :transition="{ duration: 1.5, ease: 'easeOut' }"
            :colors="colors"
            class="p-4  h-full max-h[300px] max-w-[340px] transition-transform relative hover:scale-105 hover:bg-[var(--border-color-light)]/50 hover:cursor-pointer flex flex-col"
            @touchstart.passive="touchStartY = $event.touches[0].clientY"
            @touchend.passive="(e) => handleTouchEnd(e, item.link)"
            @click="router.push(back(item.link))"
        >
          <img v-if="item.image?.url !== '' && item.image?.url !== undefined" :src="resolveMediaUrl(item.image.url)" v-bind="imgAttrs(resolveMediaUrl(item.image.url), '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')"
               :alt="imgAlt(resolveMediaUrl(item.image.url), item.title)" class="w-full h-48 object-cover rounded"/>
          <h3 class="mt-4 text-xl font-bold text-[var(--text-primary)]">
            {{ item.title }}
          </h3>
          <p class="text-sm text-[var(--text-secondary)] flex-grow mt-2 line-clamp-2" v-if="item.description">
            {{ item.description }}
          </p>
          <div class="text-sm mt-4 font-semibold text-left mr-6 text-[var(--text-secondary)]">{{ item.price }}</div>
          <div class="h-10"></div>
          <div class="w-full flex items-center justify-center" v-if="false">
            <WebButton class="max-w-42" v-if="item.callToAction" @clicked="() => router.push(back(item.link))">
              {{ item.callToAction }}
            </WebButton>
          </div>
        </component>
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed} from "vue";
import WebButton from "@/components/WebButton.vue";
import router from "@/router/index.js";
import SectionContainer from "@/components/SectionContainer.vue";
import {resolveMediaUrl} from "@/composables/images.js";
import {useThemedComponent} from "@/composables/useThemedComponent.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const cardComponent = useThemedComponent("card");

const props = defineProps({
  title: {type: String, required: true},
  description: {type: String, default: ""},
  items: {type: Array, required: true}, // Can be products, team members, blog posts, etc.
  colors: {type: Object, required: true},
  columns: {type: Number, default: 3}, // Control how many columns
  backgroundVariant: {type: Number, default: 1} // Control background color
});

// Dynamic styles using colors prop
const cssVars = computed(() => ({
  "--bg-light": props.colors.lightest,
  "--bg-dark": props.colors.darkest,
  "--card-light-1": props.colors.whitest,
  "--card-light-2": props.colors.lightest,
  "--bg-card-dark": props.colors.dark, // Darker, but not the darkest background
  "--border-color-light": props.colors.accent,
  "--border-color-dark": props.colors.accentDark,
  "--text-primary": "var(--theme-text-heading)",
  "--text-secondary": "var(--theme-text-body)"
}));
let touchStartY = 0;
const handleTouchEnd = (e, link) => {
  if (!e.changedTouches?.length) return;
  const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartY);
  if (deltaY < 10) {
    e.preventDefault?.();
    router.push(back(link));
  }
};
// Determine background class for section
const computedBackgroundClass = computed(() =>
    "bg-[var(--bg-light)] dark2:bg-[var(--bg-dark)]"
);

// Determine grid column classes dynamically
const gridColumns = computed(() => {
  return `sm:grid-cols-2 md:grid-cols-${props.columns} lg:grid-cols-${props.columns}`;
});
</script>

<style scoped>
/* Smooth hover effect */
.shadow-lg:hover {
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.1);
}
</style>
